import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from '../../RootStore';
import { hospitalService } from '../../../services/modules/hospital/hospital.service';
import {
  IHospitalRecordParam,
  IHospitalRecordResult,
  IHospitalResult,
  ITag,
  ITagNumberColor,
} from '../../../models/hospital/HospitalModel';
import { hospitalRecordService } from '../../../services/modules/hospitalRecord/hospitalRecord.service';
import { toast } from 'react-toastify';
import { GridApi } from 'ag-grid-community';
import { IQrResult } from '../../../models/hospital/QrModel';
import { RsValue } from '../../../models/base/BaseModel';
import { QrcodeResult } from 'html5-qrcode/esm/core';
import { signalrService } from '../../../services/signalr';

interface ODataParams {
  $filter?: string;
  $orderby?: string;
  $skip?: number;
  $top?: number;
  $select?: string;
  $count?: boolean;
}

interface GridParams {
  startRow: number;
  endRow: number;
  filterModel: any;
  sortModel: any;
}
export class HospitalFilesStore {
  rootStore: RootStore;
  selectedHospital: IHospitalResult | null = null;
  hospitalList: IHospitalResult[] = [];
  selectedFile: IHospitalRecordResult | null = null;
  error: string | null = null;
  gridApi: GridApi<IHospitalRecordResult> | null = null;
  hospitalRecordFromQr: QrcodeResult | null = null;
  selectedTag: ITag | null = null;
  tags: ITag[] = [];
  tagColors: ITagNumberColor[] = [];
  private tagColorsIntervalId: any = null;
  private tagColorsUpdatedHandler?: () => void;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  setSelectedTag = (tag: ITag | null) => {
    this.selectedTag = tag;
  };
  getTagColors = async (): Promise<void> => {
    try {
      if (!this.selectedTag?.id) {
        runInAction(() => (this.tagColors = []));
        return;
      }
      const response = await hospitalService.getTagColorsByTagId(this.selectedTag.id);
      runInAction(() => {
        this.tagColors = response || [];
      });
    } catch (error) {
      console.error('Error fetching tag colors:', error);
    }
  };
  startLiveTagColors = (intervalMs: number = 1000) => {
    // Prefer SignalR over polling
    this.stopLiveTagColors();
    this.getTagColors();
    const token = localStorage.getItem('jwtToken') || sessionStorage.getItem('jwtToken') || '';
    signalrService
      .connect(() => token)
      .then(() => {
        this.tagColorsUpdatedHandler = () => {
          this.getTagColors();
          this.refreshSelectedTag();
        };
        signalrService.on('TagColorsUpdated', this.tagColorsUpdatedHandler);
      })
      .catch(err => console.error('SignalR connect error', err));
    // Fallback: very light polling if connection fails will be skipped
  };
  stopLiveTagColors = () => {
    if (this.tagColorsIntervalId) {
      clearInterval(this.tagColorsIntervalId);
      this.tagColorsIntervalId = null;
    }
    if (this.tagColorsUpdatedHandler) {
      try {
        signalrService.off('TagColorsUpdated', this.tagColorsUpdatedHandler);
      } catch {}
      this.tagColorsUpdatedHandler = undefined;
    }
    signalrService.disconnect().catch(() => {});
  };
  refreshSelectedTag = async (): Promise<void> => {
    if (this.selectedHospital && this.selectedTag) {
      const response = await hospitalService.getTagsByHospitalId(this.selectedHospital.id);
      const selectedTag = response.find(x => x.id === this.selectedTag?.id);

      if (selectedTag) {
        this.setSelectedTag(selectedTag);
      } else {
        this.setSelectedTag(null);
      }
    }
  };
  getTagsByHospitalId = async (hospitalId: number): Promise<void> => {
    try {
      const response = await hospitalService.getTagsByHospitalId(hospitalId);
      runInAction(() => {
        this.tags = response || [];
      });
    } catch (error) {
      console.error('Error fetching tags:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'An unknown error occurred';
      });
    }
  };

  setGridApi = (api: GridApi<IHospitalRecordResult>) => {
    this.gridApi = api;
  };

  setSelectedHospital = (hospital: IHospitalResult | null) => {
    this.selectedHospital = hospital;
    if (hospital?.id) {
      this.getTagsByHospitalId(hospital.id);
    }
  };

  refreshGrid = () => {
    if (this.gridApi) {
      this.gridApi.refreshServerSide();
      this.gridApi.refreshInfiniteCache();
    }
  };

  setSelectedFile = (file: IHospitalRecordResult) => {
    this.selectedFile = file;
  };

  setHospitals = (hospitals: IHospitalResult[]) => {
    this.hospitalList = hospitals;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  reset = () => {
    this.selectedHospital = null;
    this.selectedFile = null;
    this.error = null;
    this.selectedTag = null;
    this.tags = [];
    this.tagColors = [];
    this.stopLiveTagColors();
  };

  listHospitalRecord = async (
    params: GridParams
  ): Promise<{ rows: IHospitalRecordResult[]; lastRow: number; count: number }> => {
    try {
      const odataParams: ODataParams = {
        $count: true,
        $skip: params.startRow,
        $top: params.endRow - params.startRow,
      };

      if (params.filterModel) {
        const filters = Object.entries(params.filterModel).map(([field, filter]: [string, any]) => {
          const numericFields = ['id', 'hospitalId'];
          if (numericFields.includes(field)) {
            return `${field} eq ${filter.filter}`;
          }
          return `contains(${field}, '${filter.filter}')`;
        });

        if (filters.length > 0) {
          odataParams.$filter = filters.join(' and ');
        }
      }

      if (params.sortModel && params.sortModel.length > 0) {
        const { colId, sort } = params.sortModel[0];
        odataParams.$orderby = `${colId} ${sort === 'asc' ? 'asc' : 'desc'}`;
      }
      if (this.selectedHospital?.id) {
        const hospitalId = parseInt(this.selectedHospital.id.toString(), 10);
        const hospitalFilter = `hospitalId eq ${hospitalId}`;
        odataParams.$filter = odataParams.$filter
          ? `${odataParams.$filter} and ${hospitalFilter}`
          : hospitalFilter;
      }

      const response: any = await hospitalRecordService.listHospitalRecords({
        params: odataParams,
      });

      return runInAction(() => {
        if (response) {
          const totalCount = response['@odata.count'] || 0;
          return {
            rows: response.value || [],
            lastRow: totalCount,
            count: totalCount,
          };
        }
        return {
          rows: [],
          lastRow: 0,
          count: 0,
        };
      });
    } catch (error) {
      console.error('Error fetching hospital records:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'An unknown error occurred';
      });
      throw error;
    }
  };
  newRowManuel = async (newRow: IHospitalRecordParam) => {
    try {
      const response: any = await hospitalRecordService.createHospitalRecord({
        ...newRow,
        hospitalId: this.selectedHospital?.id || 0,
      });

      if (response.error) {
        return response;
      }
      this.refreshGrid();
      // Tag renkleri ve seçili etiket bilgisi anında güncellensin
      await this.getTagColors();
      await this.refreshSelectedTag();
      return response;
    } catch (error) {
      console.error('Error creating hospital record:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'An unknown error occurred';
      });
      throw error;
    }
  };

  readFromQrCode = async (qrCode: string): Promise<RsValue<IQrResult>> => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response: any = await hospitalRecordService.readFromQrCode({ qrCodeUrl: qrCode });

      if (response.error) {
        runInAction(() => {
          this.error = response.message;
        });
        return response;
      }
      runInAction(() => {
        this.error = null;
      });

      return response;
    } catch (error) {
      console.error('QR kod okuma hatası:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Bilinmeyen bir hata oluştu';
        this.rootStore.uiStore.setLoading(false);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  readFromKn = async (knNumber: string): Promise<RsValue<IQrResult>> => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response: any = await hospitalRecordService.searchByKnNo({ kno: knNumber });

      if (response.error) {
        runInAction(() => {
          this.error = response.message;
        });
        return response;
      }
      runInAction(() => {
        this.error = null;
      });

      return response;
    } catch (error) {
      console.error('KN kod okuma hatası:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Bilinmeyen bir hata oluştu';
        this.rootStore.uiStore.setLoading(false);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  selectedRecordToDelete: number | null = null;

  setSelectedRecordToDelete(id: number | null) {
    this.selectedRecordToDelete = id;
  }

  deleteRecord = async (id: number) => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response: any = await hospitalRecordService.delete(id);

      if (response.error) {
        runInAction(() => {
          this.error = response.message;
        });
        return response;
      }

      runInAction(() => {
        this.error = null;
        this.refreshGrid();
      });

      return response;
    } catch (error) {
      console.error('Silme hatası:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Bilinmeyen bir hata oluştu';
        this.rootStore.uiStore.setLoading(false);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  selectedRecord: IHospitalRecordResult | null = null;

  setSelectedRecord(record: IHospitalRecordResult | null) {
    this.selectedRecord = record;
  }

  async updateRecord(id: number, data: Partial<IHospitalRecordParam>) {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response: any = await hospitalRecordService.update(id, {
        hospitalId: this.selectedHospital?.id,
        ...data,
      });

      if (response.error) {
        runInAction(() => {
          this.error = response.message;
        });
        toast.error(response.message || 'Güncelleme başarısız');
        return response;
      }

      runInAction(() => {
        this.error = null;
        this.refreshGrid();
      });

      // Güncellemeden sonra da senkronize et
      await this.getTagColors();
      await this.refreshSelectedTag();

      toast.success('Kayıt başarıyla güncellendi');
      return response;
    } catch (error) {
      console.error('Güncelleme hatası:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Bilinmeyen bir hata oluştu';
        toast.error('Güncelleme sırasında bir hata oluştu');
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  }

  async searchFieldSuggestions(fieldName: string, searchTerm: string): Promise<string[]> {
    try {
      if (!this.selectedHospital?.id) {
        return [];
      }

      const odataParams: ODataParams = {
        $filter: `startswith(tolower(${fieldName}), tolower('${searchTerm}'))`,
        $select: fieldName,
        $orderby: `${fieldName} asc`,
        $top: 5,
        $count: true,
      };

      const response: any = await hospitalRecordService.listHospitalRecords({
        params: odataParams,
      });

      if (response && response.value) {
        return Array.from(
          new Set(
            response.value
              .map((item: any) => item[fieldName])
              .filter((value: any) => value != null && value !== '')
          )
        );
      }

      return [];
    } catch (error) {
      console.error('Error searching suggestions:', error);
      return [];
    }
  }

  async addHospitalTagPrefix(params: { hospitalId: number; prefix: string }) {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response = await hospitalService.addHospitalTagPrefix(params);
      runInAction(() => {
        if (response.error != true) this.refreshGrid();
      });
      return response;
    } catch (error) {
      console.error('Error adding hospital tag prefix:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'An unknown error occurred';
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  }
}
